/**
 * Multi-level sort state for the validation queue.
 *
 * The `sort` URL param is an ordered, comma-separated list of
 * `<field>_asc` / `<field>_desc` levels — primary first, later levels breaking
 * ties inside it. A single value is the format the endpoint has always
 * accepted, so existing links keep working and simply produce one level.
 *
 * This module is the only place that format is read or written. The grid
 * headers, the summary line and the sort dialog all go through it, which is
 * what stops those three surfaces disagreeing about what is sorted.
 *
 * Decision: PDR-055 — sorting is an ordered list of levels.
 * See docs/decisions/PDR-055-validation-queue-sorts-on-ordered-levels.md
 */

export interface SortLevel {
  field: string;
  desc: boolean;
}

/**
 * Beyond three levels nobody can read the result, and the backend pays another
 * pass over the whole queue per level. Mirrors MAX_SORT_LEVELS in
 * backend/apps/processing/views.py.
 */
export const MAX_SORT_LEVELS = 3;

/**
 * Parse the `sort` param into ordered levels.
 *
 * Anything unusable is skipped rather than throwing — an unknown field, a token
 * with no direction suffix, a field already taken, or a level past the cap. A
 * hand-edited URL should show an unsorted page, not a broken one.
 *
 * @param sort  Raw param value, e.g. 'ai_category_1_name_asc,review_status_desc'.
 * @param allowed  Optional whitelist; levels naming anything else are dropped.
 */
export function parseSort(sort: string, allowed?: readonly string[]): SortLevel[] {
  const levels: SortLevel[] = [];
  const seen = new Set<string>();

  for (const raw of (sort ?? '').split(',')) {
    const token = raw.trim();

    let field: string;
    let desc: boolean;
    if (token.endsWith('_desc')) {
      field = token.slice(0, -5);
      desc = true;
    } else if (token.endsWith('_asc')) {
      field = token.slice(0, -4);
      desc = false;
    } else {
      continue;
    }

    if (!field || seen.has(field)) continue;
    if (allowed && !allowed.includes(field)) continue;

    seen.add(field);
    levels.push({ field, desc });
    if (levels.length >= MAX_SORT_LEVELS) break;
  }

  return levels;
}

/** Serialise levels back to the param value. Empty list → '' so it can be dropped. */
export function formatSort(levels: SortLevel[]): string {
  return levels.map((l) => `${l.field}_${l.desc ? 'desc' : 'asc'}`).join(',');
}

/**
 * Apply a header click.
 *
 * @param additive  true for shift-click — add this column as a further level,
 *                  or cycle it in place without disturbing the others.
 *                  false for a plain click — "just sort by this", which
 *                  replaces the whole sort (PDR-055). That is the behaviour of
 *                  every spreadsheet and data grid, and the summary line is
 *                  what makes the replacement visible.
 *
 * Cycling in both modes runs ascending → descending → removed.
 */
export function toggleLevel(
  levels: SortLevel[],
  field: string,
  additive: boolean,
): SortLevel[] {
  const existing = levels.find((l) => l.field === field);

  if (additive) {
    if (!existing) {
      // Silently refusing would look broken, so the dialog and the header
      // disable the control at the cap rather than relying on this guard.
      if (levels.length >= MAX_SORT_LEVELS) return levels;
      return [...levels, { field, desc: false }];
    }
    if (!existing.desc) {
      return levels.map((l) => (l.field === field ? { ...l, desc: true } : l));
    }
    return levels.filter((l) => l.field !== field);
  }

  const isOnlyLevel = levels.length === 1 && levels[0].field === field;
  if (!isOnlyLevel) return [{ field, desc: false }];
  if (!levels[0].desc) return [{ field, desc: true }];
  return [];
}
